/**
 * CursorOverlay — Renders remote users' cursors on the canvas.
 *
 * Cursor positions are stored in canvas (world) space and put on screen with the local
 * view. The view is read live (`getView`), so the cursors stay on their spot of the canvas
 * while this user pans or zooms: React only learns the view once such a gesture has ended.
 * They are placed by writing transforms, not by rendering.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { ViewportTransform } from '../types';
import { RemoteCursor } from '../hooks/useCursorSync';
import { collaboratorTone } from '../hooks/usePresence';

interface CursorOverlayProps {
  cursors: RemoteCursor[];
  /** The view as React has it (set when a pan or zoom ends). */
  transform: ViewportTransform;
  /** The view right now, also in the middle of a pan or zoom. Without it the cursors follow `transform`. */
  getView?: () => ViewportTransform;
  /** Offset of the canvas container from the viewport (e.g., toolbar height) */
  containerOffset?: { x: number; y: number };
}

/** How long the cursors go on following the view after the last input: a glide started by it takes about 400 ms. */
const FOLLOW_MS = 600;

/** The layer holding the cursors is moved to where the canvas's origin is on screen. */
export const layerTransform = (view: ViewportTransform, offsetX: number, offsetY: number): string =>
  `translate3d(${view.x + offsetX}px, ${view.y + offsetY}px, 0)`;

/** A cursor's place inside that layer. */
export const cursorTransform = (cursor: { x: number; y: number }, scale: number): string =>
  `translate3d(${cursor.x * scale}px, ${cursor.y * scale}px, 0)`;

export const CursorOverlay: React.FC<CursorOverlayProps> = ({ cursors, transform, getView, containerOffset }) => {
  const ox = containerOffset?.x ?? 0;
  const oy = containerOffset?.y ?? 0;
  const layerRef = useRef<HTMLDivElement>(null);
  const cursorElements = useRef(new Map<string, HTMLDivElement>());
  // What placing reads, in a ref: the frame loop below keeps running across renders
  const latest = useRef({ cursors, transform, getView, ox, oy });
  latest.current = { cursors, transform, getView, ox, oy };
  const placed = useRef<{ x: number; y: number; scale: number; ox: number; oy: number } | null>(null);

  /** Puts the layer, and every cursor when asked or after a zoom, where the view has them now. True when the view had moved. */
  const place = useCallback((everyCursor: boolean): boolean => {
    const layer = layerRef.current;
    if (!layer) {
      // The next layer is a new element, with nothing placed on it
      placed.current = null;
      return false;
    }
    const now = latest.current;
    const view = now.getView ? now.getView() : now.transform;
    const last = placed.current;
    const moved = !last || last.x !== view.x || last.y !== view.y || last.scale !== view.scale || last.ox !== now.ox || last.oy !== now.oy;
    const zoomed = !last || last.scale !== view.scale;
    if (moved) layer.style.transform = layerTransform(view, now.ox, now.oy);
    if (zoomed || everyCursor) {
      for (const cursor of now.cursors) {
        const element = cursorElements.current.get(cursor.userId);
        if (!element) continue;
        // A zoom moves every cursor at once: eased, they would trail the canvas
        element.style.transition = zoomed ? 'none' : '';
        element.style.transform = cursorTransform(cursor, view.scale);
      }
    }
    placed.current = { x: view.x, y: view.y, scale: view.scale, ox: now.ox, oy: now.oy };
    return moved;
  }, []);

  // After every render: a cursor moved, one came or went, or a pan or zoom ended
  useLayoutEffect(() => { place(true); });

  // Between renders the view moves without React knowing (a pan, a zoom, a glide): the
  // cursors follow it frame by frame for as long as it moves, starting from any input
  useEffect(() => {
    if (!getView) return;
    let frame = 0;
    let until = 0;
    const tick = () => {
      frame = 0;
      if (place(false)) until = performance.now() + FOLLOW_MS;
      if (performance.now() < until) frame = requestAnimationFrame(tick);
    };
    const follow = () => {
      until = performance.now() + FOLLOW_MS;
      if (!frame) frame = requestAnimationFrame(tick);
    };
    const inputs = ['pointerdown', 'pointermove', 'pointerup', 'wheel', 'keydown'] as const;
    inputs.forEach(name => window.addEventListener(name, follow, { capture: true, passive: true }));
    follow();
    return () => {
      inputs.forEach(name => window.removeEventListener(name, follow, { capture: true }));
      if (frame) cancelAnimationFrame(frame);
    };
  }, [getView, place]);

  if (cursors.length === 0) return null;

  return (
    // On the canvas and under everything that lies over it: its bars and drawers (z 40 and up),
    // the Edit page, every dialog and page. A cursor points at the canvas, not at this user's panels.
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-30">
      <div ref={layerRef} className="absolute left-0 top-0" style={{ willChange: 'transform' }}>
        {cursors.map((c) => (
          <div
            key={c.userId}
            ref={(element) => {
              if (element) cursorElements.current.set(c.userId, element);
              else cursorElements.current.delete(c.userId);
            }}
            // The user's tone, the same as on their avatar: the arrow and the name tag take it
            data-tone={collaboratorTone(c.userId)}
            className="absolute left-0 top-0 transition-transform duration-75 ease-linear"
            style={{ willChange: 'transform' }}
          >
            {/* Cursor arrow SVG: the tone, outlined in ink so that it shows on cream and on white */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
            >
              <path
                d="M3 2L17 10L10 11.5L7 18L3 2Z"
                style={{ fill: 'var(--tone)', stroke: 'var(--ink)' }}
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>

            {/* Name tag */}
            <div className="absolute left-4 top-4 max-w-[160px] truncate px-2 py-0.5 rounded-md bg-[color:var(--tone)] text-2xs font-semibold text-ink shadow-hair">
              {c.displayName}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
